/**
 * The review queue: every open finding across every document, worst first, with the
 * source pixels beside the value being corrected.
 *
 * There is no cross-document findings endpoint yet, so this composes the two that do
 * exist — the document listing, then `/exceptions` per parsed document, fetched
 * concurrently. That is N+1 requests, and it is honest about the ceiling: the scan is
 * capped and the page says how many documents it looked at. A dedicated endpoint is the
 * fix if the cap ever bites, not a bigger cap.
 */
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useListQuery } from "../lib/useListQuery";
import { useQueries, useQuery } from "@tanstack/react-query";
import { download, get } from "../lib/api";
import { money } from "../lib/format";
import type { DocumentList, Exception, ExceptionList } from "../lib/types";
import { Shell } from "../components/Shell";
import { Crop } from "../components/Crop";
import { CorrectField } from "../components/CorrectField";
import {
  Button,
  Chip,
  Empty,
  Eyebrow,
  Failed,
  Loading,
  Panel,
  Metric,
  Segmented,
  SEVERITY_TONE,
} from "../components/ui";

/** How many parsed documents one sweep of the queue looks at. */
const SCAN = 40;

const SEVERITY_RANK: Record<string, number> = { high: 0, medium: 1, low: 2 };

/**
 * A stable identity for one finding. Not its position: correcting a row resolves its
 * finding, every later item shifts up, and an index-keyed panel would re-bind to a
 * different row under the reviewer's cursor.
 */
function identify(item: QueueItem): string {
  return [item.documentId, item.rule, item.row_id ?? "doc", item.fields.join("+")].join(":");
}

type Filter = "all" | "high" | "medium" | "low";

interface QueueItem extends Exception {
  documentId: string;
  filename: string | null;
}

export function Review() {
  // Same substitution as Jobs: the severity filter lives in the URL. `openRow` stays
  // local — which row is expanded is not worth a link.
  const query = useListQuery();
  const filter = (query.get("severity") ?? "all") as Filter;
  const setFilter = (next: Filter) => query.set({ severity: next === "all" ? null : next });
  const [openRow, setOpenRow] = useState<string | null>(null);

  const documents = useQuery({
    queryKey: ["review", "documents"],
    queryFn: () => get<DocumentList>(`/v1/documents?limit=${SCAN}`),
  });

  const parsed = useMemo(
    () => (documents.data?.documents ?? []).filter((doc) => doc.status === "succeeded"),
    [documents.data],
  );

  const perDocument = useQueries({
    queries: parsed.map((doc) => ({
      queryKey: ["review", "exceptions", doc.id],
      queryFn: () => get<ExceptionList>(`/v1/documents/${doc.id}/exceptions`),
    })),
  });

  const loading = documents.isPending || perDocument.some((query) => query.isPending);

  const items: QueueItem[] = useMemo(() => {
    const all: QueueItem[] = [];
    perDocument.forEach((query, index) => {
      const doc = parsed[index];
      if (!doc || !query.data) return;
      for (const exception of query.data.exceptions) {
        all.push({ ...exception, documentId: doc.id, filename: doc.filename });
      }
    });
    return all.sort((a, b) => {
      const bySeverity = (SEVERITY_RANK[a.severity] ?? 9) - (SEVERITY_RANK[b.severity] ?? 9);
      if (bySeverity !== 0) return bySeverity;
      return Math.abs(b.incurred_cents ?? 0) - Math.abs(a.incurred_cents ?? 0);
    });
  }, [perDocument, parsed]);

  const shown = filter === "all" ? items : items.filter((item) => item.severity === filter);
  const counts = {
    high: items.filter((item) => item.severity === "high").length,
    medium: items.filter((item) => item.severity === "medium").length,
    low: items.filter((item) => item.severity === "low").length,
  };

  return (
    <Shell fill crumb={<span className="text-text">review</span>}>
      <div className="flex shrink-0 flex-wrap items-end justify-between gap-4">
        <div>
          <Eyebrow n="04">verify</Eyebrow>
          <h2 className="mt-2 text-[1.5rem] leading-none tracking-tighter uppercase">Review queue</h2>
          <p className="mt-2 max-w-[70ch] text-[12.5px] leading-relaxed text-dim">
            Every open finding, worst first, with the pixels it came from. Corrections are
            append-only — the extracted value is kept beside the corrected one, and the pair
            becomes a labelled example.
          </p>
        </div>
        <Button onClick={() => void download("/v1/labels", "labels.json")}>export labels</Button>
      </div>

      <div className="hairgrid shrink-0 grid-cols-2 lg:grid-cols-4">
        <Metric label="open" value={items.length} note={`${parsed.length} documents`} />
        <Metric
          label="high"
          value={counts.high}
          valueClass={counts.high ? "text-crit" : "text-ok"}
          note="wrong, not merely missing"
        />
        <Metric label="medium" value={counts.medium} valueClass={counts.medium ? "text-warn" : ""} note="worth a look" />
        <Metric label="low" value={counts.low} valueClass={counts.low ? "text-info" : ""} note="context" />
      </div>

      <Panel
        fill
        title="queue"
        aside={
          <span className="font-mono text-[10px] tracking-[0.14em] text-faint uppercase">
            newest {SCAN} documents
          </span>
        }
        actions={
          <Segmented options={["all", "high", "medium", "low"] as const} value={filter} onChange={setFilter} />
        }
      >
        {documents.isError ? (
          <Failed error={documents.error} what="the queue" />
        ) : loading ? (
          <Loading what="findings across every document" />
        ) : shown.length === 0 ? (
          <Empty
            title={items.length === 0 ? "nothing open" : `no ${filter} findings`}
            hint={
              items.length === 0
                ? "Every check passed on every parsed document in range. Nothing is waiting on a person."
                : "Change the filter to see the rest."
            }
          />
        ) : (
          <ul>
            {shown.map((item) => (
              <QueueRow
                key={identify(item)}
                item={item}
                open={openRow === identify(item)}
                onToggle={() => setOpenRow(openRow === identify(item) ? null : identify(item))}
              />
            ))}
          </ul>
        )}
      </Panel>
    </Shell>
  );
}

/** One finding, expanding into the crop plus the correction control for its field. */
function QueueRow({
  item,
  open,
  onToggle,
}: {
  item: QueueItem;
  open: boolean;
  onToggle: () => void;
}) {
  // A finding names the fields it implicates; the first is the one to correct.
  const field = item.fields[0] ?? "";

  return (
    <li className="border-b border-hair-soft last:border-b-0">
      <button
        onClick={onToggle}
        className="flex w-full items-center gap-3 px-4 py-2.5 text-start transition-colors hover:bg-hair-soft"
      >
        <i
          className="h-8 w-[3px] shrink-0"
          style={{
            background:
              item.severity === "high"
                ? "var(--crit)"
                : item.severity === "medium"
                  ? "var(--warn)"
                  : "var(--info)",
          }}
        />
        <Chip tone={SEVERITY_TONE[item.severity] ?? "mut"}>{item.rule}</Chip>
        <span className="font-mono text-[11.5px] whitespace-nowrap">
          {item.claim_number ?? <span className="text-faint">document</span>}
        </span>
        <span className="min-w-0 flex-1 truncate text-[12.5px] text-dim">{item.message}</span>
        <span className="tnum hidden font-mono text-[11.5px] whitespace-nowrap sm:inline">
          {money(item.incurred_cents) ?? <span className="text-warn">unknown</span>}
        </span>
        <span className="font-mono text-[10px] tracking-[0.14em] text-faint uppercase">
          {open ? "close" : "open"}
        </span>
      </button>

      {open && (
        <div className="grid gap-4 border-t border-hair bg-ground-soft px-4 py-4 lg:grid-cols-2">
          <div>
            <p className="eyebrow mb-2">// what to fix</p>
            <p className="text-[12.5px] leading-relaxed text-dim">{item.message}</p>
            <dl className="mt-3 flex flex-col gap-1.5 font-mono text-[11px]">
              <div className="flex gap-2">
                <dt className="text-faint">document</dt>
                <dd>
                  <Link
                    to={`/documents/${item.documentId}`}
                    className="text-gold transition-colors hover:text-gold-soft"
                  >
                    {item.filename ?? item.documentId.slice(0, 8)} →
                  </Link>
                </dd>
              </div>
              <div className="flex gap-2">
                <dt className="text-faint">fields</dt>
                <dd className="text-dim">{item.fields.join(", ") || "—"}</dd>
              </div>
              <div className="flex gap-2">
                <dt className="text-faint">page</dt>
                <dd className="text-dim">{item.page === null ? "—" : item.page + 1}</dd>
              </div>
            </dl>

            <div className="mt-4">
              <p className="eyebrow mb-2">// correction</p>
              {item.row_id && field ? (
                <CorrectField rowId={item.row_id} field={field} current="" />
              ) : (
                <p className="text-[12px] text-dim">
                  This is a document-level finding — there is no single row to correct. Open the
                  document to see what the parse did with the whole table.
                </p>
              )}
            </div>
          </div>

          <div className="border border-hair">
            <p className="eyebrow border-b border-hair px-3 py-2">// source pixels</p>
            {item.row_id ? (
              <Crop rowId={item.row_id} />
            ) : (
              <p className="px-3 py-3 text-[12px] text-dim">
                No row to crop — this finding is about the document as a whole.
              </p>
            )}
          </div>
        </div>
      )}
    </li>
  );
}
